import React, { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { ChevronLeft, ChevronRight, Calendar, X, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { flattenErrors } from "@/utils/formErrorUtils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/Popover";
import { Button } from "../ui/Button";

// ─── Constants ────────────────────────────────────────────────────────────────

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Parse any incoming format into { year, month } (1-based month; month may be
 * null for year-only dates) or null. Accepts everything the backend's
 * ResumeDate.to_iso() can emit, plus legacy picker formats:
 *   "YYYY-MM"     ← primary stored format
 *   "YYYY-MM-DD"  ← backend day-precision (day dropped)
 *   "YYYY"        ← backend year-only (month null)
 *   "MM-YYYY"     ← backwards-compat from old picker
 *   "MM/YYYY"     ← legacy backend
 *   "present" / "Present" ← handled by caller
 */
const parseValue = (rawValue) => {
  // String-safe: never assume the field value is a string (could be null,
  // a number, or an unexpected object) — coerce and trim before matching.
  if (rawValue === null || rawValue === undefined) return null;
  const value = String(rawValue).trim();
  if (!value || value.toLowerCase() === "present") return null;

  // YYYY-MM or YYYY-MM-DD or full ISO (e.g. 2019-01-15T00:00:00) — day/time ignored
  let m = value.match(/^(\d{4})-(\d{1,2})(?:[-T].*)?$/);
  if (m) {
    const month = Number(m[2]);
    return { year: Number(m[1]), month: month >= 1 && month <= 12 ? month : null };
  }

  // YYYY  (year-only)
  if (/^\d{4}$/.test(value)) return { year: Number(value), month: null };

  // MM-YYYY  (backwards-compat)
  m = value.match(/^(\d{1,2})-(\d{4})$/);
  if (m) return { year: Number(m[2]), month: Number(m[1]) };

  // MM/YYYY  (legacy backend)
  m = value.match(/^(\d{1,2})\/(\d{4})$/);
  if (m) return { year: Number(m[2]), month: Number(m[1]) };

  return null;
};

/** { year, month } → "YYYY-MM" (or "YYYY" if month-less) stored value */
const toStoredValue = ({ year, month }) => (month ? `${year}-${String(month).padStart(2, "0")}` : String(year));

/** { year, month } → "Jan 2022" (or "2022" if month-less) display label */
const toDisplayLabel = ({ year, month }) => (month ? `${MONTHS[month - 1]} ${year}` : String(year));

// ─── MonthYearPanel ───────────────────────────────────────────────────────────

const MonthYearPanel = ({ selected, onChange, onPresent, allowPresent }) => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const [showYearDropdown, setShowYearDropdown] = useState(false);

  const [viewYear, setViewYear] = useState(selected?.year ?? currentYear);

  const isSelected = (m) => selected?.year === viewYear && selected?.month === m;

  const isFuture = (m) => viewYear > currentYear || (viewYear === currentYear && m > currentMonth);

  return (
    <div className="p-3 w-64 select-none">
      {/* ── Year navigation ── */}
      <div className="relative flex items-center justify-between mb-3">
        <Button type="button" onClick={() => setViewYear((y) => y - 1)} className="p-1.5 rounded hover:bg-background-secondary text-text-primary transition">
          <ChevronLeft size={15} />
        </Button>

        <div className="relative">
          <Button
            type="button"
            onClick={() => setShowYearDropdown((prev) => !prev)}
            className="text-sm font-semibold text-text-primary px-3 py-1 flex justify-center items-center gap-2 rounded hover:bg-background-secondary transition"
          >
            {viewYear} <ChevronDown size={15} />
          </Button>

          {showYearDropdown && (
            <div
              className="
                absolute top-full left-1/2 -translate-x-1/2 mt-2
                w-24 max-h-52 overflow-y-auto text-text-primary
                rounded-md border border-border-primary
                bg-background-primary shadow-lg z-50 custom-scrollbar
            "
            >
              {Array.from({ length: currentYear - 1970 + 1 }, (_, i) => currentYear - i).map((year) => (
                <Button
                  key={year}
                  type="button"
                  onClick={() => {
                    setViewYear(year);
                    setShowYearDropdown(false);
                  }}
                  className={cn(
                    "w-full px-3 py-2 text-sm text-left rounded-none transition hover:bg-background-secondary",
                    year === viewYear && "bg-background-secondary text-text-primary",
                  )}
                >
                  {year}
                </Button>
              ))}
            </div>
          )}
        </div>

        <Button
          type="button"
          onClick={() => setViewYear((y) => Math.min(y + 1, currentYear))}
          disabled={viewYear >= currentYear}
          className="p-1.5 rounded hover:bg-background-secondary text-text-primary transition disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronRight size={15} />
        </Button>
      </div>

      {/* ── Month grid ── */}
      <div className="grid grid-cols-3 gap-1.5">
        {MONTHS.map((label, i) => {
          const month = i + 1;
          const future = isFuture(month);
          const active = isSelected(month);

          return (
            <Button
              key={label}
              type="button"
              disabled={future}
              onClick={() => onChange({ year: viewYear, month })}
              className={cn(
                "py-1.5 text-xs rounded-md font-medium transition",
                active ? "bg-primary text-white" : "text-text-primary hover:bg-background-secondary",
                future && "opacity-30 cursor-not-allowed",
              )}
            >
              {label}
            </Button>
          );
        })}
      </div>

      {/* ── "Set as Present" — end-date only ── */}
      {allowPresent && (
        <Button
          type="button"
          onClick={onPresent}
          className="mt-3 w-full py-1.5 text-xs font-semibold rounded-md border border-border-primary text-text-primary hover:bg-background-secondary transition"
        >
          Set as Present
        </Button>
      )}
    </div>
  );
};

// ─── RHFMonthYearPicker ───────────────────────────────────────────────────────

/**
 * Click-to-open month/year picker for React Hook Form.
 *
 * Behaviour:
 *  • Clicking anywhere on the field opens the month/year panel
 *  • No manual text input — selection only
 *  • Stored value  → "YYYY-MM"  (e.g. "2022-07")   sent to backend
 *  • Display label → "Jul 2022"                      shown in UI
 *  • Present stored → "present"  (lowercase)         sent to backend
 *  • Present display → "Present" (capitalised)        shown in UI
 *
 * Props:
 *  @param {string}  name         - RHF field name
 *  @param {string}  label        - Input label text
 *  @param {string}  placeholder  - Placeholder when empty
 *  @param {boolean} allowPresent - Show "Set as Present" button (end-date fields)
 *  @param {string}  className    - Wrapper class override
 *  @param {boolean} required    - Whether the field is mandatory (shows * in label)
 */
const RHFMonthYearPicker = ({ name, label, placeholder = "Select month & year", allowPresent = false, className = "", required = false, disabled = false }) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const flattened = flattenErrors(errors);
  const hasError = !!flattened[name];

  const [open, setOpen] = useState(false);

  return (
    <div className={className} data-name={name}>
      {label && (
        <label className="block text-sm mb-1.5 text-text-secondary">
          {label}
          {required && <span className="text-red-500 font-bold text-[16px] ml-1">*</span>}
        </label>
      )}

      <Controller
        name={name}
        control={control}
        defaultValue=""
        render={({ field }) => {
          const isPresent = String(field.value ?? "").toLowerCase() === "present";
          const parsed = parseValue(field.value);

          // What to show inside the button
          const displayLabel = isPresent
            ? "Present" // always capitalised in UI
            : parsed
              ? toDisplayLabel(parsed) // e.g. "Jul 2022"
              : null;

          const handleMonthSelect = (val) => {
            field.onChange(toStoredValue(val)); // → "YYYY-MM"
            setOpen(false);
          };

          const handlePresent = () => {
            field.onChange("present"); // lowercase for backend
            setOpen(false);
          };

          const handleClear = (e) => {
            e.stopPropagation(); // don't re-open picker
            field.onChange("");
          };

          return (
            <Popover open={open} onOpenChange={setOpen}>
              {/* ── Trigger — full width, entire area clickable ── */}
              <PopoverTrigger asChild>
                <button
                  type="button"
                  disabled={disabled}
                  className={cn(
                    `w-full h-11
                                        flex items-center justify-between
                                        px-4 py-2.5
                                        rounded-lg border
                                        bg-background-form/40
                                        text-sm font-normal
                                        hover:bg-background-primary
                                        focus:outline-none focus:ring-1 focus:ring-border-primary
                                        transition-all duration-150`,
                    hasError ? "border-red-500" : "border-border-primary",
                    displayLabel ? "text-text-primary" : "text-text-muted",
                    disabled && "opacity-50 cursor-not-allowed",
                  )}
                >
                  {/* Left: date label or placeholder */}
                  <span>{displayLabel ?? placeholder}</span>

                  {/* Right: clear icon (when value set) + chevron */}
                  <div className="flex items-center gap-1.5">
                    {field.value ? (
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={handleClear}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") handleClear(e);
                        }}
                        className="p-0.5 rounded text-text-muted hover:text-text-primary hover:bg-background-secondary transition"
                      >
                        <X size={13} />
                      </span>
                    ) : null}
                    <Calendar size={14} className="opacity-50" />
                  </div>
                </button>
              </PopoverTrigger>

              {/* ── Picker panel ── */}
              <PopoverContent align="start" className="p-0 rounded-lg border border-border-primary shadow-lg bg-background-primary">
                <MonthYearPanel selected={parsed} allowPresent={allowPresent} onChange={handleMonthSelect} onPresent={handlePresent} />
              </PopoverContent>
            </Popover>
          );
        }}
      />

      {/* Validation error */}
      {hasError && <p className="text-red-400 text-xs mt-1">{flattened[name]?.message}</p>}
    </div>
  );
};

export default RHFMonthYearPicker;
